import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Pressable,
  TextInput,
  Image,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import Svg, { Path, Circle } from 'react-native-svg';
import { COLORS, FONTS } from '../constants/config';
import { notificationsApi } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useResponsive } from '../hooks';
import { useLanguage } from '../contexts/LanguageContext';
import ConfirmModal from './ConfirmModal';
import NotificationPanel from './NotificationPanel';
import { useNotification } from '../contexts/NotificationContext';

// Logo — horizontal Crown Prince Office logo (matches Figma header)
const logoHorizontal = require('../../assets/logo/logo-horizental.png');
const logoHorizontalWhite = require('../../assets/logo/logo-Horizental-white.png');

// ============================================================
// SVG Icon Components
// ============================================================

const SearchIcon = ({ size = 20, color = COLORS.textLight }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M21 21L16.65 16.65" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const SettingsIcon = ({ size = 20, color = COLORS.iconMuted }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const BellIcon = ({ size = 20, color = COLORS.iconMuted }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const SunIcon = ({ size = 20, color = COLORS.iconMuted }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" pointerEvents="none">
    <Path d="M12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 2V4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 20V22" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M4.93 4.93L6.34 6.34" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M17.66 17.66L19.07 19.07" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M2 12H4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M20 12H22" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M6.34 17.66L4.93 19.07" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M19.07 4.93L17.66 6.34" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const MoonIcon = ({ size = 20, color = COLORS.iconMuted }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none" pointerEvents="none">
    <Path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Back Arrow Icon (pointing right for RTL - رجوع)
const BackArrowIcon = ({ size = 24, color = COLORS.iconMuted }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 18L15 12L9 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EllipsisVerticalIcon = ({ size = 18, color = COLORS.iconMuted }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Circle cx="12" cy="5" r="1.5" fill={color} />
    <Circle cx="12" cy="12" r="1.5" fill={color} />
    <Circle cx="12" cy="19" r="1.5" fill={color} />
  </Svg>
);

const UserIcon = ({ size = 22, color = COLORS.iconMuted }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const LogOutIcon = ({ size = 16, color = COLORS.danger }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M16 17L21 12L16 7" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M21 12H9" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Module-level debounce state (shared across all Navbar instances)
// ============================================================
let globalLastFetchTime = 0;
let globalIntervalId: NodeJS.Timeout | null = null;
let globalNotificationCount = 0;
const globalListeners: Set<(count: number) => void> = new Set();

// Push an authoritative unread count to every Navbar instance (used after the
// notification panel marks items read, so the badge updates immediately).
function broadcastNotificationCount(count: number) {
  globalNotificationCount = count;
  globalListeners.forEach((listener) => listener(count));
}

// ============================================================
// Navbar Props Interface
// ============================================================

interface NavbarProps {
  title?: string;
  notificationCount?: number;
  onSearch?: (query: string) => void;
  onNotificationPress?: () => void;
  onFilterPress?: () => void;
  onThemeToggle?: () => void;
  showSearch?: boolean;
  showFilter?: boolean;
}

// ============================================================
// Navbar Component — Figma ArHeader layout (no drawer)
// RTL visual order (right → left): logo | divider | title — search — icons | user block
// ============================================================

export default function Navbar({
  title,
  notificationCount: externalNotificationCount,
  onSearch,
  onNotificationPress,
  showSearch = true,
}: NavbarProps) {
  const navigation = useNavigation();
  const { isAuthenticated, user, logout } = useAuth();
  const { showSuccess } = useNotification();
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [userMenuVisible, setUserMenuVisible] = useState(false);
  const [notifPanelVisible, setNotifPanelVisible] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const userMenuRef = useRef<View>(null);
  const bellRef = useRef<View>(null);

  // Close the user menu / notification panel when clicking anywhere outside
  // them (web). RN-web View refs resolve to DOM elements, so contains() works.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!userMenuVisible && !notifPanelVisible) return;
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const inUserMenu = (userMenuRef.current as any)?.contains?.(target);
      const inBell = (bellRef.current as any)?.contains?.(target);
      if (userMenuVisible && !inUserMenu) setUserMenuVisible(false);
      if (notifPanelVisible && !inBell) setNotifPanelVisible(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [userMenuVisible, notifPanelVisible]);

  // Use provided title or fall back to translated system name
  const displayTitle = title || t('auth.systemName');

  // Check if we can go back in navigation history
  const canGoBack = navigation.canGoBack();

  // Check if user is executive (متخذ القرار) - they don't see notifications
  const isExecutive = user?.profile?.user_type === 'executive';
  const userRole = user?.profile?.user_type || '';
  const [internalNotificationCount, setInternalNotificationCount] = useState(0);
  const { isDark, toggleTheme, colors } = useTheme();
  const { isTablet } = useResponsive();

  // Use external count if provided, otherwise use internal count
  const notificationCount = externalNotificationCount ?? internalNotificationCount;

  const DEBOUNCE_MS = 5000; // Minimum 5 seconds between requests
  const POLLING_INTERVAL_MS = 60000; // 60 seconds between polls

  // Shared fetch function that updates all Navbar instances
  const fetchNotificationCount = useCallback(async () => {
    if (!isAuthenticated || isExecutive) {
      return;
    }

    // Global debounce - prevent fetching more than once per DEBOUNCE_MS across ALL Navbar instances
    const now = Date.now();
    if (now - globalLastFetchTime < DEBOUNCE_MS) {
      return;
    }
    globalLastFetchTime = now;

    try {
      const response = await notificationsApi.getUnreadCount();
      const count = response.unread_count || 0;
      globalNotificationCount = count;
      // Notify all listeners (other Navbar instances)
      globalListeners.forEach(listener => listener(count));
    } catch (error) {
      // Silently fail - don't show error for notification count
    }
  }, [isAuthenticated, isExecutive]);

  // Subscribe to global notification count updates
  useEffect(() => {
    if (!isAuthenticated || isExecutive) {
      return;
    }

    // Register this component as a listener
    const listener = (count: number) => {
      setInternalNotificationCount(count);
    };
    globalListeners.add(listener);

    // Initialize with current global count
    if (globalNotificationCount > 0) {
      setInternalNotificationCount(globalNotificationCount);
    }

    // Only fetch if no recent fetch (global debounce)
    const now = Date.now();
    if (now - globalLastFetchTime >= DEBOUNCE_MS) {
      fetchNotificationCount();
    }

    // Start global interval only if not already running
    if (!globalIntervalId) {
      globalIntervalId = setInterval(fetchNotificationCount, POLLING_INTERVAL_MS);
    }

    return () => {
      // Remove this component's listener
      globalListeners.delete(listener);

      // Clear global interval only when no more listeners
      if (globalListeners.size === 0 && globalIntervalId) {
        clearInterval(globalIntervalId);
        globalIntervalId = null;
      }
    };
  }, [fetchNotificationCount, isAuthenticated, isExecutive]);

  const { width: windowWidth } = useWindowDimensions();
  const isSmallScreen = windowWidth < 768;
  const isMediumScreen = isTablet;

  const handleSearch = () => {
    if (onSearch) {
      onSearch(searchQuery);
    }
  };

  const handleNotificationPress = () => {
    if (onNotificationPress) {
      onNotificationPress();
      return;
    }
    setUserMenuVisible(false);
    setNotifPanelVisible((v) => !v);
  };

  const handleHomePress = () => {
    // A guardian's home is the list of children they are responsible for, not
    // the initiative dashboard.
    (navigation as any).navigate(userRole === 'guardian' ? 'Guardian' : 'Talents');
  };

  // Settings gear → Settings screen (account info, language, theme, logout)
  const handleSettingsPress = () => {
    (navigation as any).navigate('Settings');
  };

  // Get user type display with translation
  const getUserTypeDisplay = () => {
    const typeKeys: Record<string, string> = {
      strategy_team: 'userTypes.strategyTeam',
      org_employee: 'userTypes.orgEmployee',
      org_executive: 'userTypes.orgExecutive',
      executive: 'userTypes.executive',
      superuser: 'userTypes.superuser',
      coordinator: 'userTypes.coordinator',
      student: 'userTypes.student',
      guardian: 'userTypes.guardian',
    };
    const typeKey = typeKeys[userRole];
    return typeKey ? t(typeKey) : user?.profile?.user_type_display || t('userTypes.orgEmployee');
  };

  // Role-based profile route shown inside the user dropdown
  const profileRoute =
    userRole === 'student'
      ? { screen: 'StudentProfile', label: t('navigation.myProfile') }
      : userRole === 'guardian'
        // A guardian has no student record of their own — their landing page
        // is the list of children the account is responsible for.
        ? { screen: 'Guardian', label: t('navigation.myChildren') }
        : userRole === 'coordinator'
          ? { screen: 'CoordinatorProfile', label: t('navigation.coordinatorProfile') }
          : null;

  const handleProfilePress = () => {
    setUserMenuVisible(false);
    if (profileRoute) {
      (navigation as any).navigate(profileRoute.screen);
    }
  };

  const handleLogoutConfirm = async () => {
    setShowLogoutModal(false);
    await logout();
    showSuccess(t('auth.logoutSuccess'));
  };

  // Dynamic theme styles
  const themedStyles = {
    header: {
      backgroundColor: colors.card,
      borderBottomColor: colors.cardBorder,
    },
    searchChip: {
      backgroundColor: colors.gray100,
    },
    searchChipText: {
      color: colors.gray700,
    },
    searchInputWrapper: {
      backgroundColor: colors.card,
      borderColor: colors.cardBorder,
    },
    searchInput: {
      color: colors.gray700,
    },
    headerTitle: {
      color: colors.textPrimary,
    },
    userBlock: {
      backgroundColor: isDark ? colors.backgroundAlt : '#F5F5F6',
    },
    divider: {
      backgroundColor: colors.cardBorder,
    },
    dropdownMenu: {
      backgroundColor: colors.card,
      borderColor: colors.cardBorder,
    },
  };

  return (
    <View style={[styles.header, themedStyles.header, isSmallScreen && styles.headerSmall]}>
      {/* Leftmost (LTR order): user block with dropdown */}
      <View ref={userMenuRef} style={styles.userBlockWrapper}>
        <TouchableOpacity
          style={[styles.userBlock, themedStyles.userBlock]}
          onPress={() => setUserMenuVisible(!userMenuVisible)}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel={getUserTypeDisplay()}
        >
          <EllipsisVerticalIcon size={18} color={colors.textLight} />
          {!isSmallScreen && (
            <View style={styles.userInfo}>
              <Text style={[styles.userType, { color: colors.textPrimary }]} numberOfLines={1}>
                {getUserTypeDisplay()}
              </Text>
              <Text style={[styles.userEmail, { color: colors.textMuted }]} numberOfLines={1}>
                {user?.email || ''}
              </Text>
            </View>
          )}
          <View style={styles.userIconBox}>
            <UserIcon size={26} color={colors.iconMuted} />
          </View>
        </TouchableOpacity>

        {/* User dropdown menu */}
        {userMenuVisible && (
          <View style={[styles.dropdownMenu, themedStyles.dropdownMenu]}>
            {profileRoute && (
              <TouchableOpacity style={styles.dropdownItem} onPress={handleProfilePress}>
                <Text style={[styles.dropdownItemText, { color: colors.textSecondary }]}>
                  {profileRoute.label}
                </Text>
                <UserIcon size={16} color={colors.iconMuted} />
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={styles.dropdownItem}
              onPress={() => {
                setUserMenuVisible(false);
                setShowLogoutModal(true);
              }}
            >
              <Text style={[styles.dropdownItemText, { color: colors.danger }]}>{t('auth.logout')}</Text>
              <LogOutIcon size={16} color={colors.danger} />
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Middle: icons (left) — search (center) — title (right) */}
      <View style={styles.middleRow}>
        <View style={styles.iconsGroup}>
          {/* Settings gear — account info, language, theme */}
          <Pressable
            style={({ pressed }) => [
              styles.headerIconBtn,
              pressed && { backgroundColor: colors.gray100 },
              Platform.OS === 'web' && ({ cursor: 'pointer' } as any),
            ]}
            onPress={handleSettingsPress}
            accessibilityRole="button"
            accessibilityLabel={t('navigation.settings')}
          >
            <SettingsIcon size={20} color={colors.iconMuted} />
          </Pressable>
          {/* Dark/Light mode toggle */}
          <Pressable
            style={({ pressed }) => [
              styles.headerIconBtn,
              pressed && { backgroundColor: colors.gray100 },
              Platform.OS === 'web' && ({ cursor: 'pointer' } as any),
            ]}
            onPress={toggleTheme}
            accessibilityRole="button"
            accessibilityLabel={isDark ? t('settings.lightMode') : t('settings.darkMode')}
          >
            {isDark ? <SunIcon size={20} color={colors.iconMuted} /> : <MoonIcon size={20} color={colors.iconMuted} />}
          </Pressable>
          {/* Notifications bell with real count - hidden for executive users */}
          {!isExecutive && (
            <View ref={bellRef} style={styles.bellWrapper}>
              <TouchableOpacity style={styles.headerIconBtn} onPress={handleNotificationPress}>
                <View style={styles.notificationBadge}>
                  <BellIcon size={20} color={colors.iconMuted} />
                  {notificationCount > 0 && (
                    <View style={styles.badge}>
                      <Text style={styles.badgeText}>{notificationCount > 99 ? '99+' : notificationCount}</Text>
                    </View>
                  )}
                </View>
              </TouchableOpacity>
              <NotificationPanel
                visible={notifPanelVisible}
                onClose={() => setNotifPanelVisible(false)}
                onCountChange={broadcastNotificationCount}
              />
            </View>
          )}
        </View>

        {/* Search - hide on small screens */}
        {showSearch && !isSmallScreen ? (
          <View style={[styles.searchInputWrapper, themedStyles.searchInputWrapper, isMediumScreen && styles.searchInputWrapperMedium]}>
            <TouchableOpacity style={[styles.searchChip, themedStyles.searchChip]} onPress={handleSearch}>
              <Text style={[styles.searchChipText, themedStyles.searchChipText]}>{t('navbar.searchAction')}</Text>
            </TouchableOpacity>
            <TextInput
              style={[styles.searchInput, themedStyles.searchInput]}
              placeholder={t('navbar.searchPlaceholder')}
              placeholderTextColor={colors.textLight}
              value={searchQuery}
              onChangeText={setSearchQuery}
              onSubmitEditing={handleSearch}
            />
            <SearchIcon size={18} color={colors.textLight} />
          </View>
        ) : (
          <View />
        )}

        {/* Title + Back button */}
        <View style={styles.titleGroup}>
          {!isSmallScreen && (
            <Pressable onPress={handleHomePress} style={Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : undefined}>
              <Text style={[styles.headerTitle, themedStyles.headerTitle]} numberOfLines={1}>
                {displayTitle}
              </Text>
            </Pressable>
          )}
          {canGoBack && (
            <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()} accessibilityLabel={t('common.back')}>
              <BackArrowIcon size={22} color={colors.iconMuted} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* Divider */}
      <View style={[styles.divider, themedStyles.divider]} />

      {/* Rightmost: logo */}
      <Pressable
        onPress={handleHomePress}
        style={[styles.logoBox, isSmallScreen && styles.logoBoxSmall]}
        accessibilityRole="button"
        accessibilityLabel={t('auth.systemName')}
      >
        <Image
          source={isDark ? logoHorizontalWhite : logoHorizontal}
          style={styles.logoImage}
          resizeMode="contain"
        />
      </Pressable>

      {/* Logout confirmation */}
      <ConfirmModal
        visible={showLogoutModal}
        title={t('auth.logout')}
        message={t('auth.logoutConfirm')}
        confirmText={t('auth.logout')}
        cancelText={t('common.cancel')}
        onConfirm={handleLogoutConfirm}
        onCancel={() => setShowLogoutModal(false)}
      />
    </View>
  );
}

// ============================================================
// Styles — Figma ArHeader: white bg, #E2E8F0 bottom border, px16 py8
// ============================================================

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: COLORS.white,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
    zIndex: 100,
  },
  headerSmall: {
    paddingHorizontal: 12,
    gap: 10,
  },
  // --- User block (ArMenu Item) ---
  userBlockWrapper: {
    position: 'relative',
    zIndex: 200,
  },
  userBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    minHeight: 36,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: '#F5F5F6',
  },
  userInfo: {
    alignItems: 'flex-end',
    width: 150,
  },
  userType: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  userEmail: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'right',
  },
  userIconBox: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: 4,
    minWidth: 180,
    borderWidth: 1,
    borderRadius: 8,
    paddingVertical: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    zIndex: 300,
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  dropdownItemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
  },
  // --- Middle row ---
  middleRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  iconsGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  headerIconBtn: {
    padding: 8,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  bellWrapper: {
    position: 'relative',
    zIndex: 150,
  },
  notificationBadge: {
    position: 'relative',
  },
  badge: {
    position: 'absolute',
    top: -6,
    right: -6,
    backgroundColor: COLORS.danger,
    borderRadius: 10,
    minWidth: 16,
    height: 16,
    paddingHorizontal: 3,
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeText: {
    color: COLORS.white,
    fontSize: 9,
    fontFamily: FONTS.medium,
  },
  // --- Search (ArInput) ---
  searchInputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingLeft: 2,
    paddingRight: 12,
    paddingVertical: 2,
    minHeight: 36,
    width: 320,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  searchInputWrapperMedium: {
    width: 220,
  },
  searchChip: {
    backgroundColor: '#F1F5F9',
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: 8,
  },
  searchChipText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#0F172A',
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: COLORS.gray700,
    textAlign: 'right',
    paddingHorizontal: 8,
    ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {}),
  },
  // --- Title ---
  titleGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerTitle: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  backButton: {
    padding: 6,
  },
  // --- Divider + logo ---
  divider: {
    width: 1,
    alignSelf: 'stretch',
    backgroundColor: '#E2E8F0',
  },
  logoBox: {
    width: 210,
    height: 72,
    paddingHorizontal: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoBoxSmall: {
    width: 120,
    height: 44,
    paddingHorizontal: 4,
  },
  logoImage: {
    width: '100%',
    height: '100%',
  },
});
